import { Component, inject, OnInit } from '@angular/core';
import { PageTitleComponent } from '@shared/components/page-title/page-title.component';
import { DatePipe, NgForOf, NgIf } from '@angular/common';
import { ChartComponent } from 'ng-apexcharts';
import { getResourceById, Resources } from '@app/resources';
import { FormControl, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { SimplebarAngularModule } from "simplebar-angular";
import { CountUpModule } from "ngx-countup";
import { MoneyFormatPipe } from '@common/pipe/money-format.pipe';
import { Observable } from 'rxjs';
import urlJoin from 'url-join';
import { environment } from '@environments/environment';
import { HttpClient } from '@angular/common/http';
import { NgOptionComponent, NgSelectComponent } from '@ng-select/ng-select';
import { LocalizedDatePipe } from '@shared/pipes/localized-date.pipe';
import { Router } from '@angular/router';
import { downloadBlob } from '@shared/utils/file.utils';
import { InvoicesService } from '@modules/universal-billing/billing/invoice/invoices.service';
import { SweetAlertService } from '@shared/services/sweet-alert.service';
import { ConfirmationDialogService } from '@shared/services/modal/confirm-modal.service';
import { CardComponent } from '@modules/dashboard/card/card.component';
import { SkeletonModule } from '@primeng/skeleton';
import { NgbTooltip } from '@ng-bootstrap/ng-bootstrap';
import { ValueSuffixPipe } from '@shared/pipes/unit-format.pipe';
import { finalize } from 'rxjs/operators';
import { Table } from '@primeng/table';
import { SharedModule } from '@primeng/api';
import { ReferenceField } from '@common/references/reference-field';
import { PTableCellValueComponent } from '@primeng/table/components/p-table-cell-value/p-table-cell-value.component';
import { getLayoutMode } from '@app/store/layouts/layout-selector';
import { Store } from '@ngrx/store';
import { BaseComponent } from '@common/classes/base-component/base.component';

@Component({
  selector: 'app-universal-billing',
  standalone: true,
  imports: [
    PageTitleComponent,
    NgForOf,
    ChartComponent,
    FormsModule,
    TranslatePipe,
    SimplebarAngularModule,
    CountUpModule,
    MoneyFormatPipe,
    NgSelectComponent,
    ReactiveFormsModule,
    NgOptionComponent,
    LocalizedDatePipe,
    CardComponent,
    SkeletonModule,
    NgbTooltip,
    ValueSuffixPipe,
    Table,
    SharedModule,
    PTableCellValueComponent,
    NgIf,
  ],
  templateUrl: './universal-billing.component.html',
  styleUrl: './universal-billing.component.scss'
})
export class UniversalBillingComponent extends BaseComponent implements OnInit {
  translateService = inject(TranslateService);
  invoiceService = inject(InvoicesService);
  sweetAlertService = inject(SweetAlertService);
  confirmModalService = inject(ConfirmationDialogService);
  breadCrumbItems!: Array<{}>;
  num: number = 0;
  option = {
    startVal: this.num,
    useEasing: true,
    duration: 2,
    decimalPlaces: 2,
  };
  isLoading: boolean = true;
  public stackedColumnChartOptions: any;
  public structureWidget: any;
  public clientsOverview: any = {};
  public paymentActivity: any = {}; // object -> any
  invoices = [];
  dashboard = {
    weekOverview: {
      added: {
        count: 197,
        percent: '1.15%'
      },
      signed: {
        count: 634,
        percent: '1.15%'
      },
      sent: {
        count: 512,
        percent: '3.14%'
      }
    },
    paymentActivity: {
      payment: 23590,
      incomes: '584k',
      expenses: '324k'
    },
    structure: {
      invoiced: {
        count: 56236,
        percent: '+0.2%'
      },
      collected: {
        count: 12596,
        percent: '-0.7%'
      },
      ourstanding: {
        count: 1568,
        percent: '+0.4%'
      },
    },
    quickInvoice: {
      subTotal: 699.96,
      estimatedTax: 44.99,
      discount: 53.99,
      shippingCharge: 65.00,
      totalAmount: 755.96
    },
    paymentOverview: {
      received: 45070,
      due: 32400
    },
    recentTransaction: {
      recent: {
        salaryPayment: {
          date: '20 Sep, 2022',
          percent: 62.45
        },
        onlineProduct: {
          date: '28 Mar, 2022',
          percent: '45.84'
        }
      },
      yesterday: {
        maintenance: {
          date: '18 Sep, 2022',
          percent: 25.52,
        },
        busBooking: {
          date: '30 Nov, 2022',
          percent: 84.45
        },
        flightBooking: {
          date: '12 Feb, 2022',
          percent: 53.23
        },
        officeRent: {
          date: '12 Arl, 2022',
          percent: 42.63
        }
      }
    }
  };
  period = [
    {id: 'this_week', name: this.translateService.instant('DASHBOARD.CURRENTWEEK')},
    {id: 'today', name: this.translateService.instant('DASHBOARD.TODAY')},
    {id: 'last_week', name: this.translateService.instant('DASHBOARD.LASTWEEK')},
    {id: 'last_month', name: this.translateService.instant('DASHBOARD.LASTMONTH')},
    {id: 'this_year', name: this.translateService.instant('DASHBOARD.CURRENTYEAR')}
  ];
  periodTextMap = {
    this_week: {
      title: 'DASHBOARD.CURRENT_WEEKS',
      period: 'DASHBOARD.LAST_WEEK'
    },
    today: {
      title: 'DASHBOARD.TODAYS',
      period: 'DASHBOARD.YESTERDAY'
    },
    last_week: {
      title: 'DASHBOARD.LAST_WEEKS',
      period: 'DASHBOARD.BEFORE_LAST_WEEK'
    },
    last_month: {
      title: 'DASHBOARD.LAST_MONTHS',
      period: 'DASHBOARD.BEFORE_LAST_MONTH'
    },
    this_year: {
      title: 'DASHBOARD.CURRENT_YEAR',
      period: 'DASHBOARD.LAST_YEAR'
    }
  };
  translatedPeriodLabel: { title: string; period: string } = {title: '', period: ''};
  invoiceTableFields: ReferenceField[] = [
    {
      fieldName: 'invoiceId',
      label: 'INVOICES.INVOICEID'
    },
    {
      fieldName: 'client',
      label: 'INVOICES.CLIENT'
    },
    {
      fieldName: 'date',
      label: 'DASHBOARD.DATE',
      viewType: 'date'
    },
    {
      fieldName: 'actions',
      label: 'INVOICES.ACTION'
    }
  ];
  years = [];
  minYear: number = 2025
  periodControl = new FormControl('this_week');
  mode: string;
  protected httpClient = inject(HttpClient);
  protected readonly Resources = Resources;
  protected readonly Number = Number;
  private router = inject(Router);
  private store = inject(Store);

  ngOnInit(): void {
    this.store.select(getLayoutMode).subscribe((mode) => {
      this.mode = mode;
    })
    this.breadCrumbItems = [
      {label: 'MENU.UNIVERSAL_BILLING'},
      {label: 'DASHBOARD.DASHBOARD', active: true}
    ];

    const labels = [
      this.translateService.instant('DASHBOARD.INVOICED'),
      this.translateService.instant('DASHBOARD.COLLECTED'),
      this.translateService.instant('DASHBOARD.OUTSTANDING')
    ];
    this.initializeStructureWidget();
    this.initializeStackedColumnChart();

    this.clientsOverviewFilter(this.period[0]);
    this.paymentActivityFilter(2025);
    this.invoiceFilter(null);
    this.generateYears(this.minYear)
  }

  getDataByUrl = (url, params = null): Observable<any> => this.httpClient.get(
    urlJoin(environment.apiUrl, url), {params}
  );

  getWeekDays(type: string = 'current'): { dateFrom: string, dateTo: string } {
    const currentDate = new Date();
    let dateFrom: Date;
    let dateTo: Date;

    switch (type) {
      case 'today':
        dateFrom = currentDate;
        dateTo = currentDate;
        break;
      case 'last_week':
        const firstDayOfWeek = currentDate.getDate() - currentDate.getDay() + 1;
        dateFrom = new Date(currentDate.setDate(firstDayOfWeek - 7));
        dateTo = new Date(currentDate.setDate(firstDayOfWeek - 1));
        break;
      case 'last_month':
        dateFrom = new Date(currentDate.getFullYear(), currentDate.getMonth() - 1, 1);
        dateTo = new Date(currentDate.getFullYear(), currentDate.getMonth(), 1);
        break;
      case 'this_year':
        dateFrom = new Date(currentDate.getFullYear(), 0, 2);
        dateTo = new Date(currentDate.getFullYear(), 12, 1);
        break;
      case 'this_week':
        const today = currentDate.getDay();
        dateFrom = new Date(currentDate.setDate(currentDate.getDate() - today));
        dateTo = new Date(currentDate.setDate(currentDate.getDate() + 7));
        break;
      case 'current-month':
        dateFrom = new Date(currentDate.getFullYear(), currentDate.getMonth(), 2);
        dateTo = new Date(currentDate.getFullYear(), currentDate.getMonth() + 1, 1);
        break;
      default:
        dateFrom = currentDate;
        dateTo = currentDate;
        break;
    }

    const formattedDate = (date: Date) => {
      const datePipe = new DatePipe('en-US');
      return datePipe.transform(date, 'yyyy-MM-dd');
    };

    return {dateFrom: formattedDate(dateFrom), dateTo: formattedDate(dateTo)};
  }

  getYearInterval(year) {
    return {dateFrom: `${year}-01-01`, dateTo: `${year}-12-31`};
  }

  clientsOverviewFilter(period) {
    this.isLoading = true;
    this.translatedPeriodLabel = {
      title: this.translateService.instant(this.periodTextMap[period.id]?.title),
      period: this.translateService.instant(this.periodTextMap[period.id]?.period),
    };

    this.getDataByUrl('reports/dashboard/weekly-summary', {dateRange: period['id']}).pipe(
      finalize(() => this.isLoading = false)
    ).subscribe(overview => {
      if (overview) {
        this.clientsOverview = overview['data'];
        const collected = Number(this.clientsOverview?.structure?.collected || 0);
        const outstanding = Number(this.clientsOverview?.structure?.outstanding || 0);
        const balance = Number(this.clientsOverview?.structure?.balance || 0);

        const rawSeries = [collected, outstanding, balance];
        const rawLabels = [
          this.translateService.instant('DASHBOARD.COLLECTED'),
          this.translateService.instant('DASHBOARD.OUTSTANDING'),
          this.translateService.instant('DASHBOARD.INVOICED')
        ];

        const filtered = rawSeries
          .map((value, index) => ({value, label: rawLabels[index]}))
          .filter(item => item.value > 0);

        this.structureWidget.series = filtered.map(item => item.value);
        this.structureWidget.labels = filtered.map(item => item.label);

      }
    });
  }

  paymentActivityFilter(year: number) {
    this.getDataByUrl('reports/dashboard/payment-activity', this.getYearInterval(year))
      .subscribe(overview => {
        const data = overview['data'];
        this.paymentActivity = {
          paymentTotal: parseFloat(parseFloat(data.paymentTotal).toFixed(2)) || 0,
          amountPaid: parseFloat(parseFloat(data.amountPaid).toFixed(2)) || 0,
          amountUnpaid: parseFloat(parseFloat(data.amountUnpaid).toFixed(2)) || 0,
          paid: (data.paid || []).map(val => parseFloat(parseFloat(val).toFixed(2))),
          unpaid: (data.unpaid || []).map(val => parseFloat(parseFloat(val).toFixed(2)))
        };
        this.initializeStackedColumnChart(this.paymentActivity.paid, this.paymentActivity.unpaid);
      });
  }

  invoiceFilter(period) {
    this.getDataByUrl('reports/dashboard/invoice-list', period ? this.getWeekDays(period) : {}).subscribe(overview => {
      this.invoices = overview['data'];
    });
  }

  onView(id) {
    this.router.navigate([getResourceById(Resources.InvoiceDetails, id)]);
  }

  download(data) {
    this.invoiceService.downloadPdf(data?.id).subscribe({
      next: (blob) => {
        downloadBlob(blob, `${data?.invoiceId}.pdf`);
      },
      error: (error) => {
        this.sweetAlertService.error('Error loading invoice');
      },
      complete: () => {
      }
    });
  }

  delete(id: number) {
    this.confirmModalService.open().then((res) => {
      if (res) {
        this.invoiceService.delete(id).subscribe(() => {
          this.sweetAlertService.success('', 'TOAST.TOAST_DELETE').then((res) => {
            if (res['value']) {
              this.invoiceFilter(null)
            }
          });
        },
          (error) => {
          this.alertErrorMessage(error?.error?.message);
          });
      }
    });
  }

  getProfitStyle(profitType) {
    let border, background, color;

    if (profitType == 1) {
      border = '1px solid var(--in-primary)';
      color = 'var(--in-primary)';
      background = this.mode === 'light' ? '#EAFFF1' : '';
    } else {
      border = '1px solid ' + (this.mode === 'dark' ? 'var(--in-danger)' : '#FEC6D4');
      color = this.mode === 'dark' ? 'var(--in-danger)' : 'var(--in-danger)';
      background = this.mode === 'light' ? '#FFEEF3' : '';
    }

    return {border, background, color}
  }

  private initializeStackedColumnChart(paid = [], unpaid = []): void {
    const months = [
      this.translateService.instant('MONTHS.JAN'),
      this.translateService.instant('MONTHS.FEB'),
      this.translateService.instant('MONTHS.MAR'),
      this.translateService.instant('MONTHS.APR'),
      this.translateService.instant('MONTHS.MAY'),
      this.translateService.instant('MONTHS.JUN'),
      this.translateService.instant('MONTHS.JUL'),
      this.translateService.instant('MONTHS.AUG'),
      this.translateService.instant('MONTHS.SEP'),
      this.translateService.instant('MONTHS.OCT'),
      this.translateService.instant('MONTHS.NOV'),
      this.translateService.instant('MONTHS.DEC')
    ];
    this.stackedColumnChartOptions = {
      chart: {
        height: 403,
        type: 'bar',
        stacked: false,
        toolbar: {
          show: false
        },
        zoom: {
          enabled: true
        }
      },
      plotOptions: {
        bar: {
          horizontal: false,
          columnWidth: '95%',
        }
      },
      dataLabels: {
        enabled: false
      },
      series: [
        {
          name: this.translateService.instant('BUILD_DASHBOARD.INCOME'),
          data: paid
        },
        {
          name: this.translateService.instant('BUILD_DASHBOARD.OUTCOME'),
          data: unpaid
        }
      ],
      xaxis: {
        categories: months
      },
      grid: {
        xaxis: {
          lines: {
            show: false
          }
        },
        yaxis: {
          lines: {
            show: false
          }
        }
      },
      colors: ['#50C878', '#EB4C42'],
      legend: {
        show: false
      },
      fill: {
        opacity: 1
      }
    };
  }

  private initializeStructureWidget(): void {

    this.structureWidget = {
      chart: {
        height: 300,
        type: 'donut',
      },
      colors: ['#50C878', '#EB4C42', '#9457EB'],
      plotOptions: {
        pie: {
          startAngle: 25,
          donut: {
            size: '65%',
          }
        }
      },
      legend: {
        show: false,
      },
      dataLabels: {
        style: {
          fontSize: '16px',
          fontFamily: 'DM Sans,sans-serif',
          colors: undefined
        },
        background: {
          enabled: true,
          foreColor: '#fff',
          padding: 4,
          borderRadius: 2,
          borderWidth: 1,
          borderColor: '#fff',
          opacity: 1,
        },
      },
      responsive: [{
        breakpoint: 600,
        options: {
          chart: {
            height: 240
          },
          legend: {
            show: false
          },
        }
      }]
    };
  }

  generateYears(minYear){
    const currentYear = new Date().getFullYear();

    for (let year = minYear; year <= currentYear; year++) {
      this.years.push({ id: year, name: year.toString() });
    }
  }
}
